import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://seller.udyamwell.in/api';

type ExportType = 'products' | 'orders';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportType: ExportType;
  orgSellers?: any[];
}

// ─── CSV download helper ───────────────────────────────────────
function downloadCSV(data: Record<string, any>[], filename: string) {
  if (!data || data.length === 0) {
    alert('No data found for the selected filters.');
    return;
  }
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row =>
      headers
        .map(h => {
          const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
          return /[",\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;
        })
        .join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Searchable Seller Dropdown ────────────────────────────────
const SearchableSellerDropdown = ({
  sellers,
  value,
  onChange,
}: {
  sellers: any[];
  value: string;
  onChange: (val: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const filtered = sellers.filter(s =>
    (s.businessName || s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedSeller = sellers.find(s => s._id === value);
  const displayValue = value === 'all'
    ? 'All Sellers'
    : selectedSeller
      ? selectedSeller.businessName || selectedSeller.name || selectedSeller.email
      : 'Select Seller';

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white cursor-pointer flex items-center justify-between focus:outline-none focus:ring-green-500 focus:border-green-500"
      >
        <span className="truncate text-gray-700">{displayValue}</span>
        <svg className="w-4 h-4 text-gray-400 shrink-0 ml-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full top-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <input
              type="text"
              autoFocus
              placeholder="Search sellers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          {/* Options */}
          <ul className="max-h-52 overflow-auto py-1">
            <li
              className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm ${value === 'all' ? 'bg-green-100 font-medium text-green-700' : 'text-gray-700'}`}
              onClick={() => { onChange('all'); setIsOpen(false); setSearch(''); }}
            >
              All Sellers
            </li>
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400 italic">No sellers found</li>
            ) : (
              filtered.map(s => (
                <li
                  key={s._id}
                  className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm ${value === s._id ? 'bg-green-100 font-medium text-green-700' : 'text-gray-700'}`}
                  onClick={() => { onChange(s._id); setIsOpen(false); setSearch(''); }}
                >
                  {s.businessName || s.name || s.email}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Main ExportModal ──────────────────────────────────────────
const DATE_PRESETS = ['All Time', 'Last 7 Days', 'Last 30 Days', 'This Quarter', 'Year to Date', 'Custom Range'];

const PRODUCT_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'Grocery', label: 'Grocery' },
  { value: 'Fashion', label: 'Fashion' },
  { value: 'Home & Kitchen', label: 'Home & Kitchen' },
  { value: 'Agriculture', label: 'Agriculture' },
];

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, exportType, orgSellers = [] }) => {
  const [datePreset, setDatePreset] = useState('All Time');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSeller, setSelectedSeller] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem('ud-user') ?? '';
      const params: Record<string, string> = { userId };

      if (datePreset === 'Custom Range') {
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      } else if (datePreset !== 'All Time') {
        params.dateRange = datePreset;
      }

      if (selectedSeller !== 'all') params.sellerId = selectedSeller;
      if (exportType === 'products' && categoryFilter !== 'all') params.category = categoryFilter;
      if (exportType === 'orders' && statusFilter !== 'all') params.status = statusFilter;

      const endpoint = exportType === 'products' ? '/export/products' : '/export/orders';
      const response = await axios.get(`${API_URL}${endpoint}`, { params });

      if (response.data.success) {
        const now = new Date();
        const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        downloadCSV(response.data.data, `${exportType}_export_${timestamp}.csv`);
        onClose();
      } else {
        alert('Export failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Export failed: ' + (err?.response?.data?.message || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">
              Export {exportType === 'products' ? 'Products' : 'Orders'} to Excel
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
              value={datePreset}
              onChange={e => setDatePreset(e.target.value)}
            >
              {DATE_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Custom Date Pickers */}
          {datePreset === 'Custom Range' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input type="date"
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                  value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input type="date"
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                  value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
            </div>
          )}

          {/* Category Filter (Products only) */}
          {exportType === 'products' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
              >
                {PRODUCT_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Searchable Seller Dropdown */}
          {orgSellers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Seller</label>
              <SearchableSellerDropdown
                sellers={orgSellers}
                value={selectedSeller}
                onChange={setSelectedSeller}
              />
            </div>
          )}

          {/* Order Status Filter */}
          {exportType === 'orders' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Status</label>
              <select
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Accepted">Accepted</option>
                <option value="In-progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          )}

          {/* Info note */}
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-3">
            📥 Downloads as <strong>.csv</strong> — opens directly in Microsoft Excel or Google Sheets.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
